import { Box, Button, List, ListItem, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Alert } from '@api/api';
import { RelTime, SeverityChip, EmptyState } from './common';
import { endpoints } from '../api/endpoints';
import { useNotify } from './Notify';
import { useIsOwner } from '../auth/AuthProvider';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export const ALERT_KIND_LABEL: Record<string, string> = {
  down: 'Down',
  container_restarting: 'Container restarting',
  errors_spike: 'Error spike',
  backup_stale: 'Backup stale',
  disk: 'Disk space',
  tls_expiry: 'TLS expiry',
  reconcile: 'Reconcile',
  job_failed: 'Job failed',
  stats_missing: 'Stats missing',
};

export function AlertList({ alerts, showClone = true, compact, emptyTitle }: { alerts: Alert[]; showClone?: boolean; compact?: boolean; emptyTitle?: string }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const owner = useIsOwner();
  const ack = useMutation({
    mutationFn: (id: number) => endpoints.ackAlert(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      notify.ok('Alert acknowledged');
    },
    onError: (e) => notify.error(e),
  });

  if (!alerts.length)
    return emptyTitle ? <EmptyState title={emptyTitle} /> : <EmptyState icon={<CheckCircleOutlineIcon fontSize="inherit" color="success" />} title="No open alerts" text="Everything the collector checks looks fine." />;

  return (
    <List disablePadding>
      {alerts.map((a) => (
        <ListItem key={a.id} divider sx={{ px: 0, alignItems: 'flex-start', opacity: a.resolvedAt ? 0.75 : 1 }}>
          <Stack direction="row" spacing={1.5} sx={{ width: '100%' }} alignItems="flex-start">
            <Box sx={{ pt: 0.25 }}>
              <SeverityChip severity={a.severity} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {ALERT_KIND_LABEL[a.kind] ?? a.kind}
                {showClone && (
                  <>
                    {' · '}
                    {a.cloneId ? (
                      <Box component={RouterLink} to={`/clones/${a.cloneId}`} sx={{ color: 'primary.main', textDecoration: 'none' }}>
                        {a.cloneSlug}
                      </Box>
                    ) : (
                      <Box component="span" color="text.secondary">
                        server
                      </Box>
                    )}
                  </>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {a.message}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                opened <RelTime iso={a.openedAt} />
                {a.resolvedAt ? (
                  <>
                    {' · resolved '}
                    <RelTime iso={a.resolvedAt} />
                  </>
                ) : (
                  !compact && (
                    <>
                      {' · seen '}
                      <RelTime iso={a.lastSeenAt} />
                    </>
                  )
                )}
                {a.acknowledgedBy && ` · ack by ${a.acknowledgedBy}`}
              </Typography>
            </Box>
            {!a.resolvedAt && !a.acknowledgedBy && owner && (
              <Button size="small" onClick={() => ack.mutate(a.id)} disabled={ack.isPending && ack.variables === a.id}>
                Ack
              </Button>
            )}
          </Stack>
        </ListItem>
      ))}
    </List>
  );
}
